/** Statuses considered when selecting the client's current SAH support plan. */
export const SAH_SUPPORT_PLAN_STATUSES = [
  'ACTIVE',
  'IN_REVIEW',
  'PENDING_APPROVAL',
  'DRAFT',
] as const;

/**
 * Ranks SAH support-plan statuses for "current plan" selection. Lower is more
 * authoritative. DRAFT is included so a drafting plan surfaces ahead of unknown
 * statuses while still losing to ACTIVE / IN_REVIEW / PENDING_APPROVAL.
 */
export function supportPlanStatusRank(status: unknown): number {
  switch (String(status).toUpperCase()) {
    case 'ACTIVE':
      return 0;
    case 'IN_REVIEW':
      return 1;
    case 'PENDING_APPROVAL':
      return 2;
    case 'DRAFT':
      return 3;
    default:
      return 99;
  }
}

export function isSahSupportPlanStatus(status: unknown): boolean {
  return SAH_SUPPORT_PLAN_STATUSES.includes(
    String(status).toUpperCase() as (typeof SAH_SUPPORT_PLAN_STATUSES)[number],
  );
}

/** Select only from rows already admitted by the SAH server projection.
 * Template type is not a second programme authority: funding_program takes
 * precedence in the canonical source binding. This does not approve a draft.
 */
export function selectCurrentScopedSahSupportPlan<
  T extends {
    status: string | null;
    version: number | null;
    start_date: string | null;
  },
>(plans: readonly T[]): T | null {
  const date = (value: string | null) => {
    const parsed = value ? Date.parse(value) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  };
  return (
    [...plans]
      .filter((plan) => isSahSupportPlanStatus(plan.status))
      .sort(
        (left, right) =>
          supportPlanStatusRank(left.status) - supportPlanStatusRank(right.status) ||
          Number(right.version ?? 1) - Number(left.version ?? 1) ||
          date(right.start_date) - date(left.start_date),
      )[0] ?? null
  );
}
